import React, { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, Label } from 'recharts';
import { getCookie } from "../../services/utils.js";
import loginData from '../../pages/BugList/userLoginIds.json';

/**
 * BugDistributionPieChart Component
 * Displays a pie chart showing the distribution of bugs (Valid, Invalid, Missed)
 */
const BugDistributionPieChart = ({ bugCounts, bugsData }) => {
  // State to store the processed data for the pie chart
  const [distributionData, setDistributionData] = useState([
    { name: 'Valid Bugs', value: 0, percent: 0 },
    { name: 'Invalid Bugs', value: 0, percent: 0 },
    { name: 'Missed Bugs', value: 0, percent: 0 }
  ]);
  
  // Define colors for the pie chart sections - matching the Bug Severity Chart colors
  const COLORS = ["#5FB531", "#B73F21", "#F9E576"]; // Green for Valid, Red for Invalid, Yellow for Missed
  
  // Custom label component for the center of the pie chart - styled to match other charts
  const CenterLabel = ({ viewBox, value }) => {
    const { cx, cy } = viewBox;
    return (
      <text 
        x={cx} 
        y={cy} 
        textAnchor="middle" 
        dominantBaseline="middle"
        fontSize={14}
        fontFamily="Poppins, sans-serif"
        fill="#1A1A1A"
      >
        <tspan fontSize={14} x={cx} dy="0">Total Bugs:</tspan>
        <tspan fontSize={16} fontWeight={700} x={cx} dy="1.2em">
          {value}
        </tspan>
      </text>
    );
  };

  // Process the bug data whenever bugCounts or bugsData changes
  useEffect(() => {
    // If data is not available, set default values
    if (!bugCounts || !bugsData) {
      setDistributionData([
        { name: 'Valid Bugs', value: 0, percent: 0 },
        { name: 'Invalid Bugs', value: 0, percent: 0 },
        { name: 'Missed Bugs', value: 0, percent: 0 }
      ]);
      return;
    }
    
    // Get email from cookie to identify approved users
    const email = getCookie("email");
    const isApprovedUser = loginData.login.includes(email);

    // Calculate missed bugs (bugs raised by reviewers)
    let missedBugsCount = 0;
    
    // If user is an approved reviewer, count their bugs as missed bugs
    if (isApprovedUser) {
      // Loop through all bugs and count those created by approved users
      missedBugsCount = bugsData.filter(bug => 
        loginData.login.includes(bug.createdBy || '')
      ).length;
    }

    // Calculate valid bugs (subtracting missed bugs)
    const validBugsCount = Math.max(0, bugCounts.validBugsCount - missedBugsCount);
    
    // Calculate total and percentages
    const total = validBugsCount + bugCounts.invalidBugsCount + missedBugsCount;
    const validPercentage = total > 0 ? Math.round((validBugsCount / total) * 100) : 0;
    const invalidPercentage = total > 0 ? Math.round((bugCounts.invalidBugsCount / total) * 100) : 0;
    const missedPercentage = total > 0 ? Math.round((missedBugsCount / total) * 100) : 0;

    // Update chart data with the new format for recharts
    setDistributionData([
      { 
        name: 'Valid Bugs', 
        value: validBugsCount, 
        percent: validPercentage 
      },
      { 
        name: 'Invalid Bugs', 
        value: bugCounts.invalidBugsCount, 
        percent: invalidPercentage 
      },
      { 
        name: 'Missed Bugs', 
        value: missedBugsCount, 
        percent: missedPercentage 
      }
    ]);
  }, [bugCounts, bugsData]);

  // Calculate the total bugs for the center label
  const totalBugs = distributionData.reduce((acc, entry) => acc + entry.value, 0);

  return (
    <div className='dashboard-graph'>
    <div className="chart-container">
      <h2>Bug Distribution</h2>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        height: '100%',
        padding: '10px'
      }}>
        <PieChart width={400} height={340} margin={{ top: 25 }}>
          <Pie
            data={distributionData}
            cx={200}
            cy={150}
            outerRadius={110}
            innerRadius={50} // Adding inner radius to make it a donut chart
            paddingAngle={1}
            dataKey="value"
            label={(entry) => `${entry.name}: ${entry.percent}%`} // Format similar to Bug Severity Chart
          >
            {distributionData.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={COLORS[index % COLORS.length]} 
              />
            ))}
            
            <Label
              content={<CenterLabel value={totalBugs} />}
              position="center"
            />
          </Pie>
          <Tooltip 
            formatter={(value, name) => [
              `${value} bugs (${distributionData.find(item => item.name === name)?.percent || 0}%)`,
              name
            ]}
          />
        </PieChart>
      </div>
    </div>
    </div>
  );
};

export default BugDistributionPieChart;
